import React, { FC, useState, useMemo, useEffect } from 'react';
import { Add, Close, Save } from '@mui/icons-material';
import {
  Grid,
  MenuItem,
  TextField,
  Box,
  ButtonGroup,
  Button,
  Divider,
  Autocomplete
} from '@mui/material';
import { useQuery } from 'react-query';
import { Form, Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  getUnitsOfMeasure,
  getMaterialTasks,
  getBidFrequencies,
  putMaterialGeneralInfo,
  getMaterialClassParents
} from '../../../../fetch';
import { IDropdownResponse, IMaterialGeneralInfo, IMaterialClasses } from '../../../../models';
import { Loader, SMBSelect, SelectAsync } from '../../../../components';
import { ManageMaterialClassDropdown } from './manage-material-class-dropdown';

interface IGeneralInformation {
  generalInfo: IMaterialGeneralInfo;
  isLoadingGeneralInfo: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchGeneralInfo: () => void;
}

const GeneralInfoSchema = Yup.object().shape({
  name: Yup.string(),
  materialClassId: Yup.number(),
  unitOfMeasureId: Yup.number(),
  isActive: Yup.string(),
  bidFrequencyId: Yup.number(),
  manufacturer: Yup.string(),
  modelNumber: Yup.string(),
  quantityInterval: Yup.number()
    .integer('Only whole numbers are allowed.')
    .max(999999999, 'Only up to 9 digits are allowed.')
    .nullable(),
  roundUp: Yup.string(),
  isTaxExempt: Yup.string(),
  tasks: Yup.array(),
  materialClassSelections: Yup.array(),
  prices: Yup.array()
});

export const GeneralInformation: FC<IGeneralInformation> = ({
  generalInfo,
  isLoadingGeneralInfo,
  setDirtyForm,
  refetchGeneralInfo
}) => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { materialId }: { materialId: string } = useParams();
  const [selectedMaterialClassIdFilter, setSelectedMaterialClassIdFilter] = useState(null);
  const [selectionsCleared, setSelectionsCleared] = useState<boolean>(false);
  const [selectedTaskOptionValues, setSelectedTaskOptionValues] = useState<IDropdownResponse[]>([]);
  const [inputSelectedTaskOptions, setInputSelectedTaskOptions] = useState('');
  const [selectedValues, setSelectedValues] = useState<IMaterialClasses[]>(() => {
    if (generalInfo?.materialClassSelections) {
      return generalInfo?.materialClassSelections;
    }
    return [];
  });

  const { isLoading: isLoadingTasks, data: tasks } = useQuery<IDropdownResponse[], Error>(
    ['getMaterialTasks'],
    () => getMaterialTasks(),
    {
      onSuccess: d => {
        const options = d.filter(record => {
          return generalInfo?.tasks?.includes(record.value);
        });
        setSelectedTaskOptionValues(options);
      },
      enabled: generalInfo !== undefined
    }
  );
  const taskOptionsData = useMemo(() => tasks ?? [], [tasks]);

  const { isLoading: isLoadingMaterialClassParents, data: materialClassParents } = useQuery<
    IDropdownResponse[],
    Error
  >(
    ['material-class-parents', selectedMaterialClassIdFilter],
    async () =>
      await getMaterialClassParents(
        selectedMaterialClassIdFilter === null && !selectionsCleared
          ? generalInfo?.materialClassId
          : selectedMaterialClassIdFilter
      ),
    {
      enabled: (!!selectedMaterialClassIdFilter || !!generalInfo) && !selectionsCleared
    }
  );

  useEffect(() => {
    if (materialClassParents) {
      setSelectedValues(materialClassParents.slice().reverse());
    }
  }, [materialClassParents]);

  return (
    <>
      {isLoadingGeneralInfo && isLoadingMaterialClassParents && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingGeneralInfo && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            name: generalInfo?.name ?? '',
            materialClassId: generalInfo?.materialClassId ?? '',
            unitOfMeasureId: generalInfo?.unitOfMeasureId ?? '',
            bidFrequencyId: generalInfo?.bidFrequencyId ?? '',
            manufacturer: generalInfo?.manufacturer ?? '',
            modelNumber: generalInfo?.modelNumber ?? '',
            quantityInterval: generalInfo?.quantityInterval ?? '',
            isActive:
              generalInfo?.isActive === true ? 'Yes' : generalInfo?.isActive === false ? 'No' : '',
            roundUp:
              generalInfo?.roundUp === true ? 'Yes' : generalInfo?.roundUp === false ? 'No' : '',
            isTaxExempt:
              generalInfo?.isTaxExempt === true
                ? 'Yes'
                : generalInfo?.isTaxExempt === false
                ? 'No'
                : '',
            tasks: generalInfo?.tasks ?? [],
            materialClassSelections: generalInfo?.materialClassSelections ?? [],
            prices: generalInfo?.prices ?? []
          }}
          validationSchema={GeneralInfoSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IMaterialGeneralInfo = {
                ...values,
                materialClassId: selectedMaterialClassIdFilter ?? values.materialClassId,
                isActive: values.isActive === 'Yes' ? true : false,
                isTaxExempt:
                  values.isTaxExempt === 'Yes' ? true : values.isTaxExempt === '' ? null : false,
                roundUp: values.roundUp === 'Yes' ? true : values.roundUp === '' ? null : false,
                prices: values.prices ?? null,
                quantityInterval: values.quantityInterval === '' ? 0 : values.quantityInterval,
                modelNumber: values.modelNumber ?? '',
                manufacturer: values.manufacturer ?? ''
              };

              await putMaterialGeneralInfo(parseInt(materialId), data);
              refetchGeneralInfo();
              enqueueSnackbar('Material has been updated successfully!', {
                variant: 'success'
              });
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Something went wrong. Material was not updated.', {
                variant: 'error'
              });
            }
          }}
        >
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            if (dirty) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }

            if (
              selectedMaterialClassIdFilter !== null &&
              selectedMaterialClassIdFilter !== generalInfo?.materialClassId
            ) {
              dirty = true;
            }

            return (
              <>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}

                <Form onSubmit={handleSubmit}>
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              required
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Name"
                              name="name"
                              size="small"
                              value={values.name}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('name', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SelectAsync
                              required
                              label="Unit of Measure"
                              name="unitOfMeasureId"
                              onClear={() => {
                                setFieldValue('unitOfMeasureId', '');
                              }}
                              value={values.unitOfMeasureId}
                              hasClear
                              apiRequest={() => getUnitsOfMeasure()}
                              transformResponse={(res: IDropdownResponse[]) =>
                                res.map(r => ({
                                  label: r.description,
                                  value: r.value
                                }))
                              }
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container spacing={1}>
                              <ManageMaterialClassDropdown
                                required={true}
                                materialClassId={null}
                                depth={0}
                                setSelectedMaterialClassIdFilter={setSelectedMaterialClassIdFilter}
                                width={3}
                                selectedValues={selectedValues}
                                setSelectedValues={setSelectedValues}
                                setSelectionsCleared={setSelectionsCleared}
                              />
                            </Grid>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Manufacturer"
                              name="manufacturer"
                              size="small"
                              value={values.manufacturer}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('manufacturer', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Model"
                              name="modelNumber"
                              size="small"
                              value={values.modelNumber}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('modelNumber', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Quantity Interval"
                              name="quantityInterval"
                              size="small"
                              value={values.quantityInterval}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('quantityInterval', e.target.value)}
                              error={touched.quantityInterval && !!errors.quantityInterval}
                              helperText={touched.quantityInterval && errors.quantityInterval}
                            />
                          </Grid>
                          {values?.quantityInterval ? (
                            <Grid item xs={12} sm={6}>
                              <SMBSelect
                                disabled={values.quantityInterval ? false : true}
                                onChange={({ target: { value } }) => {
                                  setFieldValue('roundUp', value);
                                }}
                                handleClear={() => setFieldValue('roundUp', '')}
                                variant="outlined"
                                name="rounding"
                                label="Rounding"
                                id="rounding"
                                value={values.roundUp}
                              >
                                {[
                                  { value: 'Yes', shorthand: 'Yes', description: 'Yes' },
                                  { value: 'No', shorthand: 'No', description: 'No' }
                                ].map((rounding, index) => {
                                  return (
                                    <MenuItem key={`${rounding.value}`} value={rounding.value}>
                                      {rounding.description}
                                    </MenuItem>
                                  );
                                })}
                              </SMBSelect>
                            </Grid>
                          ) : null}
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              onChange={({ target: { value } }) => {
                                setFieldValue('isActive', value);
                              }}
                              handleClear={() => setFieldValue('isActive', '')}
                              variant="outlined"
                              name="isActive"
                              label="Is Material Active"
                              id="isActive"
                              value={values.isActive}
                            >
                              {[
                                { value: 'Yes', shorthand: 'Yes', description: 'Yes' },
                                { value: 'No', shorthand: 'No', description: 'No' }
                              ].map((active, index) => {
                                return (
                                  <MenuItem key={`${active.value}`} value={active.value}>
                                    {active.description}
                                  </MenuItem>
                                );
                              })}
                            </SMBSelect>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SelectAsync
                              required
                              label="Bid Frequency"
                              name="bidFrequencyId"
                              onClear={() => {
                                setFieldValue('bidFrequencyId', '');
                              }}
                              value={values.bidFrequencyId}
                              hasClear
                              apiRequest={() => getBidFrequencies()}
                              transformResponse={(res: IDropdownResponse[]) =>
                                res.map(r => ({
                                  label: r.description,
                                  value: r.value
                                }))
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              onChange={({ target: { value } }) => {
                                setFieldValue('isTaxExempt', value);
                              }}
                              handleClear={() => setFieldValue('isTaxExempt', '')}
                              variant="outlined"
                              name="isTaxExempt"
                              label="Is Tax Exempt"
                              id="isTaxExempt"
                              value={values.isTaxExempt}
                            >
                              {[
                                { value: 'Yes', shorthand: 'Yes', description: 'Yes' },
                                { value: 'No', shorthand: 'No', description: 'No' }
                              ].map((exempt, index) => {
                                return (
                                  <MenuItem key={`${exempt.value}`} value={exempt.value}>
                                    {exempt.description}
                                  </MenuItem>
                                );
                              })}
                            </SMBSelect>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Autocomplete
                              multiple
                              id="tasks"
                              value={selectedTaskOptionValues}
                              onChange={(event, newValue: any[]) => {
                                setSelectedTaskOptionValues(newValue);
                                setFieldValue(
                                  'tasks',
                                  newValue.map(val => val.value)
                                );
                              }}
                              inputValue={inputSelectedTaskOptions}
                              onInputChange={(event, newInputValue) => {
                                setInputSelectedTaskOptions(newInputValue);
                              }}
                              options={taskOptionsData}
                              getOptionLabel={option => option.description}
                              filterSelectedOptions
                              renderOption={(props, option: any) => {
                                return (
                                  <li {...props} key={`${option.value}`}>
                                    {option.description}
                                  </li>
                                );
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  disabled={isLoadingTasks}
                                  label={isLoadingTasks ? 'Loading...' : 'Available Tasks'}
                                  placeholder="Tasks"
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={materialId ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/parts/manage-materials');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};
